import { useEffect, useState } from "react";
import type { Authorization, ZReport, ZSummary } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { archiveFigures, dayName, zDateTime, zPrint, type ReportPrint } from "./reports";

interface Props {
  authorization: Authorization;
  onOpen: (print: ReportPrint) => void;
}

/**
 * Z-archief: every Z by the day it is about, the newest day first (a Z made later stands on its own day,
 * Furkan 11/10/2026); each Z can be shown and printed again, never changed.
 */
export function ArchiveTab({ authorization, onOpen }: Props) {
  const { api, report, reportsRevision } = useTill();
  const [reports, setReports] = useState<ZSummary[] | null>(null);
  const text = t.reports.archive;

  useEffect(() => {
    let current = true;
    api
      .request<{ reports: ZSummary[] }>("GET", "/device/reports/z", { staffToken: authorization.token, retry: true })
      .then((answer) => {
        if (current) setReports(answer.reports);
      })
      .catch(report);
    return () => {
      current = false;
    };
  }, [api, authorization.token, report, reportsRevision]);

  const open = (number: number, original = false) => {
    api
      .request<{ report: ZReport }>("GET", `/device/reports/z/${String(number)}${original ? "?original=1" : ""}`, {
        staffToken: authorization.token,
        retry: true,
      })
      .then((answer) => {
        onOpen(zPrint(answer.report));
      })
      .catch(report);
  };

  if (reports === null) return <div aria-busy="true" />;
  if (reports.length === 0) return <p className="note">{text.none}</p>;
  return (
    <>
      <div className="tablewrap">
        <table className="stafftbl stack zarch">
          <thead>
            <tr>
              <th>{text.columns.number}</th>
              <th>{text.columns.day}</th>
              <th>{text.columns.closed}</th>
              <th className="r">{text.columns.till}</th>
              <th className="r">{text.columns.platforms}</th>
              <th className="r">{text.columns.dayTotal}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {reports.map((row) => {
              // Kassa without the linked platforms, Platformen with them; the day total as it always was
              const figures = archiveFigures(row);
              return (
                <tr key={row.number}>
                  <td className="num">
                    <b>{row.number}</b>
                    {row.corrections > 0 && (
                      <>
                        {" "}
                        <span className="pill warn" title={text.correctedTitle(row.corrections)}>
                          {text.corrected}
                        </span>
                      </>
                    )}
                  </td>
                  <td className="grow">
                    <b>{dayName(row.businessDay)}</b>
                  </td>
                  <td className="later" data-label={text.columns.closed}>
                    {/* when, not who (Furkan, 30/10/2026: "Z raporda isim yazmayacak") */}
                    {zDateTime(row.closedAt)}
                    {row.late && (
                      <>
                        {" "}
                        <span className="pill warn">{text.late}</span>
                      </>
                    )}
                  </td>
                  <td className="r num later" data-label={text.columns.till}>
                    {formatEuro(figures.tillCents)}
                  </td>
                  <td className="r num later" data-label={text.columns.platforms}>
                    {formatEuro(figures.platformsCents)}
                  </td>
                  <td className="r num later" data-label={text.columns.dayTotal}>
                    {formatEuro(figures.dayTotalCents)}
                  </td>
                  <td className="end">
                    <button
                      type="button"
                      className="btn"
                      aria-label={text.openOf(row.number)}
                      onClick={() => {
                        open(row.number);
                      }}
                    >
                      {text.open}
                    </button>
                    {row.corrections > 0 && (
                      <>
                        {" "}
                        <button
                          type="button"
                          className="btn"
                          aria-label={text.originalOf(row.number)}
                          onClick={() => {
                            open(row.number, true);
                          }}
                        >
                          {text.original}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="note">{text.note}</p>
    </>
  );
}
